import { Button, Typography } from "@material-ui/core";
import moment from "moment";
import { summarizeCats } from "../../components/quiz-history-table/quiz-history-table";
import "./student-recent-work.scss";

const MAX_CARDS = 3;

// Same moment(row.complete) contract the history table and progress summary
// use; a missing or unparseable timestamp is "no date", never "Invalid date".
const completedAt = (q) => (q.complete && moment(q.complete).isValid() ? moment(q.complete) : null);

const hasId = (q) => q.id !== undefined && q.id !== null && q.id !== "";

// percentageCorrect serializes 0 for unfinished quizzes, so it is only shown on
// completed rows — and only when it is a real 0-100 number (0 is a valid score).
const validScore = (p) => Number.isFinite(p) && p >= 0 && p <= 100;

/**
 * Tutor-facing "recent work" strip for the mentorship student page: the
 * student's latest completed quizzes (with a review shortcut routed through
 * the caller's guarded `onReview`) and, separately, tutor-assigned quizzes the
 * history marks as not completed. Read-only over the already-fetched history
 * rows — no requests, no navigation of its own.
 */
export default function StudentRecentWork({ quizzes, onReview }) {
  const rows = (Array.isArray(quizzes) ? quizzes : []).filter(Boolean);
  const canReview = typeof onReview === "function";

  // Newest valid completion first; undated rows keep their input order at the end.
  const completed = rows
    .filter((q) => q.iscomplete === true)
    .map((q, index) => ({ q, at: completedAt(q), index }))
    .sort((a, b) => {
      if (a.at && b.at) return b.at.valueOf() - a.at.valueOf() || a.index - b.index;
      if (a.at || b.at) return a.at ? -1 : 1;
      return a.index - b.index;
    })
    .slice(0, MAX_CARDS);

  // Only an explicit `false` plus a tutor assignment counts — no inferring
  // progress from zero scores or stray timestamps.
  const unfinished = rows
    .filter((q) => q.iscomplete === false && q.assignedByTutorId)
    .slice(0, MAX_CARDS);

  if (!completed.length && !unfinished.length) return null;

  // "Latest" is only claimed for a dated row, and never falls back to an older
  // row when the newest one can't be opened.
  const latest = completed[0];
  const showLatest = canReview && latest && latest.at && hasId(latest.q);

  return (
    <section className="srw" aria-label="Recent work" data-testid="srw">
      {completed.length > 0 && (
        <div className="srw-group" data-testid="srw-completed">
          <div className="srw-group-head">
            <Typography variant="subtitle2" component="h3" className="srw-group-title">
              Recently completed
            </Typography>
            {showLatest && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                className="srw-btn srw-review-latest"
                aria-label={`Review latest quiz: ${latest.q.title || "Untitled quiz"}`}
                onClick={() => onReview(latest.q)}
                data-testid="srw-review-latest"
              >
                Review latest quiz
              </Button>
            )}
          </div>
          <ul className="srw-cards">
            {completed.map(({ q, at }, i) => {
              const title = q.title || "Untitled quiz";
              const topics = q.medicalCategorizationMap?.topics;
              const topicSummary = Array.isArray(topics) && topics.length ? summarizeCats(topics) : null;
              return (
                <li className="srw-card" key={i} data-testid="srw-completed-card">
                  <Typography variant="body2" className="srw-card-title" data-testid="srw-card-title">
                    {title}
                  </Typography>
                  <div className="srw-card-meta">
                    {at && <span data-testid="srw-card-date">Completed {at.format("ll")}</span>}
                    {validScore(q.percentageCorrect) && (
                      <span className="srw-card-score" data-testid="srw-card-score">
                        {Math.round(q.percentageCorrect)}%
                      </span>
                    )}
                  </div>
                  {topicSummary && (
                    <Typography
                      variant="caption"
                      component="p"
                      className="srw-card-topics"
                      title={topicSummary.title}
                      data-testid="srw-card-topics"
                    >
                      {topicSummary.text}
                    </Typography>
                  )}
                  {canReview && hasId(q) && (
                    <Button
                      variant="outlined"
                      size="small"
                      className="srw-btn srw-card-review"
                      aria-label={`Review quiz: ${title}`}
                      onClick={() => onReview(q)}
                      data-testid="srw-card-review"
                    >
                      Review quiz
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {unfinished.length > 0 && (
        <div className="srw-group" data-testid="srw-unfinished">
          <div className="srw-group-head">
            <Typography variant="subtitle2" component="h3" className="srw-group-title">
              Unfinished assignments
            </Typography>
          </div>
          <Typography variant="caption" component="p" className="srw-sub">
            Tutor-assigned quizzes in this history that aren&apos;t completed yet.
          </Typography>
          <ul className="srw-cards">
            {unfinished.map((q, i) => (
              <li className="srw-card srw-card--unfinished" key={i} data-testid="srw-unfinished-card">
                <Typography variant="body2" className="srw-card-title">
                  {q.title || "Untitled quiz"}
                </Typography>
                <span className="srw-status">Not completed</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
